/**
 * Router file to navigate the views of the application
 *
 * @author   pablo.barco
 * @date     2020-06-29
 *
 * @copyright 2020 Smartmatic Corp. All Rights Reserved.
 *
 * @license
 *
 * Confidential information of Smartmatic Labs. Not for disclosure or
 * distribution prior written consent. This software contains code, techniques
 * and know-how which is confidential and proprietary to Smartmatic.
 *
 * Use of this software is subject to the terms of an end user license
 * agreement.
 */

import Vue from "vue";
import VueRouter from "vue-router";

import store from "../store/store";
import LoginPage from "../components/login/LoginPage";
import WelcomeScreen from "../components/WelcomeScreen";
import ItemsNeeded from "../components/ItemsNeeded";
import SiteDetailsScreen from "../components/siteDetails/SiteDetailsScreen";
/* eslint-disable */
import SurveyQuestionsScreen from "../components/SurveyQuestionScreen/SurveyQuestionsScreen";
import SurveyFinishedSuccessfully from "../components/SurveyQuestionScreen/SurveyFinishedSuccessfully";
import About from "../components/hamburgerMenu/components/About";
import Language from "../components/hamburgerMenu/components/Language";
import Profile from "../components/hamburgerMenu/components/Profile";
import MapNavigation from "../components/siteDetails/MapNavigation";
import MainContainer from "@/components/MainContainer";
import DrillDownScreen from "../components/drillDown/DrillDownScreen";
import ForgotPassword from "../components/login/ForgotPassword";
import LoginExternal from "@/components/loginExternal/LoginExternal";
import LoginSuccess from "@/components/login/LoginSuccess";
import UserNotFoundError from "@/components/errorPages/UserNotFoundError.vue";
import { initializeFCM } from "../utils/firebase";

Vue.use(VueRouter);

const router = new VueRouter({
  routes: [
    { path: "/login", name: "Login", component: LoginPage, props: true },
    {
      path: "/loginExternal",
      name: "LoginExternal",
      component: LoginExternal,
      props: (route) => ({ authToken: route.query.authToken }),
    },
    {
      path: "/login/forgotPassword",
      name: "ForgotPassword",
      component: ForgotPassword,
    },
    { path: "/loginSuccess", name: "LoginSuccess", component: LoginSuccess },
    {
      path: "/",
      name: "main",
      component: MainContainer,
      redirect: WelcomeScreen,
      children: [
        {
          path: "",
          name: "Welcome",
          component: WelcomeScreen,
          props: true,
        },
        {
          path: "/itemsNeeded",
          name: "ItemsNeeded",
          component: ItemsNeeded,
        },
        {
          path: "/siteDetails/:assignmentId",
          name: "SiteDetailsScreen",
          component: SiteDetailsScreen,
        },
        {
          path: "/siteDetails/:assignmentId/mapNavigation",
          name: "MapNavigation",
          component: MapNavigation,
        },
        {
          path: "/surveyQuestions/:roomId/:assignmentId/:roomAssignmentId",
          name: "SurveyQuestionsScreen",
          component: SurveyQuestionsScreen,
          props: true,
        },
        {
          path: "/surveyFinished",
          name: "SurveyFinishedSuccessfully",
          component: SurveyFinishedSuccessfully,
          props: true,
        },
        { path: "/about", name: "About", component: About },
        { path: "/language", name: "Language", component: Language },
        { path: "/profile", name: "Profile", component: Profile },
        {
          path: "/drillDown",
          name: "DrillDownScreen",
          component: DrillDownScreen,
        },
      ],
    },
    {
      path: "/userNotFoundError",
      name: "UserNotFoundError",
      component: UserNotFoundError,
    },
  ],
});

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

const loginProcess = async ({ next }) => {
  if (store.getters.isKeycloakActive()) {
    // We need to login with Keycloak

    // We will wait until keycloak is ready
    while (
      Vue.prototype.$keycloak?.createLoginUrl == null ||
      Vue.prototype.$keycloak?.login == null
    ) {
      await sleep(200);
    }

    // If we're authenticated, we need to save the session data, otherwise we need to start the login progress with Keycloak.
    if (Vue.prototype.$keycloak.authenticated) {
      // Wait for FCM token resolution before making API call
      await new Promise((resolve) => {
        const intervalId = setInterval(() => {
          if (store.state.fcmToken.status !== "loading") {
            clearInterval(intervalId);
            resolve();
          }
        }, 500);
      });

      const result = await store.dispatch("loginFromKeycloak", {
        keycloak: Vue.prototype.$keycloak,
        fcmToken: store.state.fcmToken.token,
      });

      if (result.success) {
        // If the user has only one site, redirect to the site details screen
        store
          .dispatch("getSingleAssignmentByUser")
          .then((response) => {
            if (!response.success) {
              next({ name: "Welcome" });
            } else {
              if (response.data.isSingleAssignment) {
                next({
                  name: "SiteDetailsScreen",
                  params: { assignmentId: response.data.assignmentId },
                });
              } else {
                next({ name: "Welcome" });
              }
            }
          })
          .catch(() => {
            // Let's go to DataCol Page
            this.$router.replace({ name: "Welcome" });
          });
      } else {
        next({ name: "UserNotFoundError" });
      }
    } else {
      next(false);
      Vue.prototype.$keycloak.login();
    }
  } else {
    // We need to login with the normal process

    next({ name: "Login" });
  }
};

const evaluateSession = async () => {
  if (store.getters.isKeycloakActive()) {
    if (
      Vue.prototype.$keycloak?.authenticated == false &&
      store.state.loggedUser != null
    ) {
      // The session expired or was closed from keycloak console.

      // Here I need to log out the user from the backend server
      await store.dispatch("logoffUserFromKeycloak");

      const loginUrl = Vue.prototype.$keycloak.createLoginUrl();
      window.location.replace(loginUrl);

      return "relogin";
    }
  }
};

router.beforeEach(async (to, from, next) => {
  // Hides loading spinner in each navigation to prevent lock state on errors
  store.commit("hideLoading");

  if (
    to.name !== "Login" &&
    to.name !== "ForgotPassword" &&
    to.name !== "LoginExternal" &&
    to.name !== "UserNotFoundError" &&
    store.state.loggedUser == null
  ) {
    loginProcess({ next });
  } else {
    const isReLogin = await evaluateSession();
    // we continue with the normal process

    if (!isReLogin) {
      if (to.name === "LoginExternal") {
        next();
      } else if (
        store.state.loggedUser?.roles.every((rol) => rol.role === "OPERATOR")
      ) {
        // The operator can only go to LoginSuccess and access the routes from the hamburger menu
        if (
          ["About", "Language", "Profile", "LoginSuccess"].includes(to.name)
        ) {
          next();
        } else {
          next({ name: "LoginSuccess" });
        }
      } else if (
        to.name === "UserNotFoundError" &&
        store.state.loggedUser != null
      ) {
        next({ name: "Welcome" });
      } else {
        next();
      }
    }
  }
});

export default router;
